"use client"

import {zodResolver} from "@hookform/resolvers/zod"
import {useForm} from "react-hook-form"
import {z} from "zod"

import {Button} from "@/components/ui/button"
import {Form, FormControl, FormField, FormItem} from "@/components/ui/form"
import {Input} from "@/components/ui/input"
import {useMask} from "@react-input/mask";
import {postOrder} from "@/helpers/api";
import {FormDataProps} from "@/intefaces/form-data.props";
import React, {forwardRef, useRef, useState} from "react";
import {P} from "@/components/ui/P";
import {sendGTMEvent} from "@next/third-parties/google";
import cn from "classnames";
import {Checkbox} from "@/components/ui/checkbox";

interface FormOrderProps {
    address?: string;
    formname?: string;
    className?: string;
}

const FormOrder = forwardRef<HTMLDivElement, FormOrderProps>(({ address, formname, className }, ref) => {
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [agreed, setAgreed] = useState(false);
    const [showError, setShowError] = useState(false);
    const checkboxRef = useRef<HTMLButtonElement | null>(null); // Ссылка на чекбокс

    const formSchema = z.object({
        name: z.string().min(1, { message: "Введите ваше имя" }),
        phone: z.string().regex(/^\+7\s\(\d{3}\)\s\d{3}-\d{2}-\d{2}$/, "Некорректный номер")
    });

    const inputRef = useMask({ mask: '+7 (___) ___-__-__', replacement: { _: /\d/ } });

    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            name: "",
            phone: ""
        },
    });

    async function onSubmit(values: FormDataProps) {
        if (!agreed) {
            setShowError(true);
            checkboxRef.current?.focus();
            return;
        }

        try {
            const newValues = { ...values, formname, address };
            const response = await postOrder(newValues);
            sendGTMEvent({ event: 'form-order', value: 'form-order' });
            // @ts-ignore
             if (response.ok) {
                setIsSubmitted(true);
                form.reset({ name: "", phone: "" });
                setAgreed(false);
            } else {
                throw new Error("Network response was not ok");
            }
        } catch (error) {
            console.error('Ошибка при отправке данных:', error);
        }
    }

    if (isSubmitted) {
        return (
            <div className="text-center">
                <P size={'g'} className="text-2xl font-semibold">Данные отправлены</P>
                <P size={'s'} className="text-2xl font-semibold">Менеджер свяжется с вами в ближайшее время</P>
            </div>
        );
    }

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className={cn("space-y-6", className)}>
                <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                        <FormItem>
                            <FormControl>
                                <Input placeholder="Ваше имя" {...field} type="text" />
                            </FormControl>
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                        <FormItem className="mt-8">
                            <FormControl ref={inputRef}>
                                <Input placeholder="+7 (999) 000-00-00" {...field} type="tel" />
                            </FormControl>
                        </FormItem>
                    )}
                />
                <div className="flex items-center space-x-2">
                    <Checkbox
                        id="terms"
                        checked={agreed}
                        onCheckedChange={(checked) => {
                            setAgreed(!!checked);
                            setShowError(false);
                        }}
                        ref={checkboxRef}
                    />
                    <label
                        htmlFor="terms"
                        className="text-[10px] leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                        Я согласен(а) с&nbsp;
                        <a
                            href="/privacy" // ← замените на реальный путь к политике, если нужно
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline text-primary hover:text-primary/80"
                        >
                            политикой конфиденциальности
                        </a>
                    </label>

                </div>
                {showError && (
                    <p className="text-red-500 text-xs mt-1">Подтвердите согласие с обработкой данных</p>
                )}
                <Button type="submit" className="mt-4">Отправить</Button>
            </form>
        </Form>
    );
});

FormOrder.displayName = 'FormOrder';
export default FormOrder;
